import React, { useState, useEffect, useCallback } from 'react';
import {
  useColorModeValue } from "./ui/color-mode";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  SimpleGrid,
  Badge,
  Button,
  Progress,
  Spinner,
  Icon,
  List,
  useDisclosure,
  Separator,
  Dialog,
  Portal,
} from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster';
import { FaCheck, FaCrown, FaBolt, FaArrowUp, FaLock, FaHdd } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import AppLayout from './AppLayout.jsx';

const PLAN_LABELS = {
  GRATIS: 'Gratis',
  ESTANDAR: 'Estándar',
  INTERMEDIO: 'Intermedio',
  AVANZADO: 'Avanzado',
  COFRADIA: 'Cofradía',
};

const FEATURE_LABELS = {
  perfilPublico: 'Perfil público',
  comunidad: 'Comunidad y búsquedas',
  tuWeb: 'Tu Web (micrositio)',
  dominioPropio: 'Dominio propio',
  calendario: 'Calendario',
  eventos: 'Eventos',
  contactos: 'Contactos',
  mensajeria: 'Mensajería',
  acuerdos: 'Acuerdos',
  archivoMusical: 'Archivo musical',
  musicos: 'Mis músicos',
  formaciones: 'Formaciones',
  actuaciones: 'Actuaciones',
  ensayos: 'Ensayos',
  documentos: 'Documentos',
  estadisticas: 'Estadísticas',
  objetivos: 'Objetivos',
  encuestas: 'Encuestas',
  permisos: 'Permisos',
};

const formatBytes = (bytes) => {
  const n = Number(bytes || 0);
  if (n >= 1024 * 1024 * 1024) {
    return `${(n / 1024 / 1024 / 1024).toFixed(1)} GB`;
  }
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

const formatQuotaMb = (mb) => {
  if (mb == null) return 'Ilimitado';
  if (mb >= 1024) {
    const gb = mb / 1024;
    return Number.isInteger(gb) ? `${gb} GB` : `${gb.toFixed(1)} GB`;
  }
  return `${mb} MB`;
};

const MiSuscripcion = ({ embedded = false }) => {
  
  const navigate = useNavigate();
  const { refreshUser, profile } = useAuth();
  const wrap = (node) =>
    embedded ? (
      node
    ) : (
      <AppLayout userType="banda" userName={profile?.nombre || 'Mi Banda'}>
        {node}
      </AppLayout>
    );
  const cardBg = useColorModeValue('white', 'gray.800');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(false);
  const { open: isOpen, onOpen, onClose } = useDisclosure();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authAPI.getPlan();
      setData(res.data);
    } catch {
      toaster.create({ title: 'No se pudo cargar tu suscripción', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleUpgrade = async () => {
    setUpgrading(true);
    try {
      await authAPI.upgradePlan();
      await refreshUser();
      await load();
      onClose();
      toaster.create({
        title: '¡Bienvenido al plan Avanzado!',
        description: 'Ya tienes acceso a todas las funcionalidades.',
        type: 'success',
        duration: 5000,
      });
    } catch (e) {
      toaster.create({
        title: 'No se pudo cambiar de plan',
        description: e?.response?.data?.message || 'Inténtalo de nuevo.',
        type: 'error',
      });
    } finally {
      setUpgrading(false);
    }
  };

  if (loading) {
    return wrap(
      <Box p={12} textAlign="center">
        <Spinner size="lg" color="purple.500" />
      </Box>,
    );
  }

  if (!data) {
    return wrap(<Box p={12} />);
  }

  const {
    tier,
    features,
    budgetEur,
    usage,
    period,
    canUpgradeToAvanzado,
    necesitaValidacionParaAvanzado,
  } = data;
  // IA: porcentaje del presupuesto del ciclo. Disco: ocupación / tope del plan.
  const iaPercent =
    usage && budgetEur > 0 ? (usage.iaCostEur / budgetEur) * 100 : 0;
  const quotaMb = usage?.storageQuotaMb;
  const quotaBytes =
    quotaMb == null || quotaMb < 0 ? null : quotaMb * 1024 * 1024;
  const storagePercent =
    quotaBytes == null
      ? 0
      : quotaBytes === 0
        ? (usage?.storageBytes || 0) > 0
          ? 100
          : 0
        : ((usage?.storageBytes || 0) / quotaBytes) * 100;
  const storageOverQuota = quotaBytes != null && storagePercent >= 100;
  const incluidas = Object.entries(features || {})
    .filter(([, v]) => v)
    .map(([k]) => FEATURE_LABELS[k] || k);

  const renewsOnDay = period?.renewsOnDay ?? 1;
  const nextRenewalLabel = (() => {
    if (!period?.nextRenewalAt) return null;
    try {
      return new Date(period.nextRenewalAt).toLocaleDateString('es-ES', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        timeZone: 'UTC',
      });
    } catch {
      return null;
    }
  })();
  const obrasCount = usage?.obrasCount;
  const paginasCount = usage?.paginasCount;

  return wrap(
    <Container maxW="4xl" py={{ base: embedded ? 0 : 6, md: embedded ? 2 : 10 }}>
      <VStack align="stretch" gap={6}>
        {!embedded && (
          <Box>
            <Heading size="lg" color="gray.800">Mi suscripción</Heading>
            <Text color="gray.500">
              Consulta tu plan, el consumo de IA del periodo y el almacenamiento ocupado.
            </Text>
          </Box>
        )}

        {/* Tarjeta del plan actual */}
        <Box bg={cardBg} rounded="2xl" borderWidth="1px" p={{ base: 5, md: 7 }} boxShadow="sm">
          <HStack justify="space-between" align="start" flexWrap="wrap" gap={4}>
            <VStack align="start" gap={1}>
              <HStack>
                <Icon color={tier === 'AVANZADO' ? 'purple.500' : 'gray.400'} asChild><FaCrown /></Icon>
                <Heading size="md" color="gray.800">Plan {PLAN_LABELS[tier] || tier}</Heading>
              </HStack>
              {tier === 'AVANZADO' && (
                <Badge colorPalette="purple" rounded="full" px={2}>
                  Gratis por tiempo limitado durante el lanzamiento
                </Badge>
              )}
            </VStack>
            {canUpgradeToAvanzado && (
              <Button colorPalette="purple" rounded="full" onClick={onOpen}><Icon asChild><FaArrowUp /></Icon>Pasar a Avanzado (gratis)
                              </Button>
            )}
            {necesitaValidacionParaAvanzado && (
              <Button
                variant="outline"
                colorPalette="purple"
                rounded="full"
                onClick={() => navigate('/banda/perfil')}><Icon asChild><FaLock /></Icon>Valida tu banda para Avanzado
                              </Button>
            )}
          </HStack>
          {necesitaValidacionParaAvanzado && (
            <Text fontSize="sm" color="gray.500" mt={3}>
              El plan Avanzado es gratis durante el lanzamiento para las bandas
              validadas. Valida tu banda desde tu perfil para desbloquearlo.
            </Text>
          )}

          {usage && (
            <Box mt={6}>
              <Separator mb={4} />
              <Text fontWeight="600" color="gray.700" mb={2}>Consumo de este periodo</Text>
              <Text fontSize="sm" color="gray.600" mb={4}>
                El análisis con IA (obras y páginas que añades) cuenta contra el límite
                del plan y se reinicia el día de renovación de tu suscripción (día{' '}
                {renewsOnDay} de cada mes)
                {nextRenewalLabel ? ` — próxima renovación: ${nextRenewalLabel}` : ''}.
              </Text>

              <Box>
                <HStack justify="space-between" mb={1}>
                  <HStack gap={1}>
                    <Icon color="orange.400" asChild><FaBolt /></Icon>
                    <Text fontSize="sm" fontWeight="600" color="gray.700">Inteligencia artificial</Text>
                  </HStack>
                  <Text fontSize="sm" color="gray.500">{Math.round(iaPercent)}%</Text>
                </HStack>
                <Progress.Root
                  value={Math.min(100, iaPercent)}
                  size="md"
                  rounded="full"
                  colorPalette={iaPercent >= 100 ? 'red' : iaPercent >= 80 ? 'orange' : 'purple'}>
                  <Progress.Track>
                    <Progress.Range />
                  </Progress.Track>
                </Progress.Root>
              </Box>

              {usage.exceeded && (
                <Text fontSize="sm" color="red.500" mt={4}>
                  Has alcanzado el límite de tu plan. El análisis con IA y las subidas
                  están pausados. El consumo de IA se reinicia en la próxima
                  renovación; el almacenamiento solo baja si borras archivos.
                </Text>
              )}
            </Box>
          )}
        </Box>

        {usage && (
          <Box bg={cardBg} rounded="2xl" borderWidth="1px" p={{ base: 5, md: 7 }} boxShadow="sm">
            <HStack gap={2} mb={2}>
              <Icon color="blue.400" asChild><FaHdd /></Icon>
              <Heading size="sm" color="gray.800">Almacenamiento</Heading>
            </HStack>
            <Text fontSize="sm" color="gray.600" mb={4}>
              Lo que ocupa ahora el archivo de la banda. No se reinicia cada mes: solo
              baja si borras partituras u otros archivos.
              {typeof obrasCount === 'number' && typeof paginasCount === 'number' && (
                <>
                  {' '}Ahora tienes {obrasCount} obra{obrasCount === 1 ? '' : 's'} y {paginasCount}{' '}
                  página{paginasCount === 1 ? '' : 's'}.
                </>
              )}
            </Text>
            <HStack justify="space-between" mb={1}>
              <Text fontSize="sm" fontWeight="600" color="gray.700">Ocupado</Text>
              <Text fontSize="sm" color="gray.500">
                {formatBytes(usage.storageBytes)}
                {' de '}
                {formatQuotaMb(quotaMb)}
                {quotaMb != null ? ` · ${Math.round(storagePercent)}%` : ''}
              </Text>
            </HStack>
            {quotaMb != null && (
              <Progress.Root
                value={Math.min(100, storagePercent)}
                size="md"
                rounded="full"
                colorPalette={storagePercent >= 100 ? 'red' : storagePercent >= 80 ? 'orange' : 'blue'}>
                <Progress.Track>
                  <Progress.Range />
                </Progress.Track>
              </Progress.Root>
            )}
            {storageOverQuota && (
              <Text fontSize="sm" color="red.500" mt={3}>
                Has alcanzado el límite de almacenamiento de tu plan. Borra archivos
                o mejora el plan para subir más.
              </Text>
            )}
          </Box>
        )}

        {/* Funcionalidades incluidas */}
        <Box bg={cardBg} rounded="2xl" borderWidth="1px" p={{ base: 5, md: 7 }} boxShadow="sm">
          <Heading size="sm" color="gray.800" mb={4}>Incluido en tu plan</Heading>
          <SimpleGrid columns={{ base: 1, sm: 2 }} gap={2}>
            {incluidas.map((f) => (
              <HStack key={f} gap={2} align="center">
                <Icon color="green.500" asChild><FaCheck /></Icon>
                <Text fontSize="sm" color="gray.700">{f}</Text>
              </HStack>
            ))}
          </SimpleGrid>
        </Box>
      </VStack>

      {/* Modal de confirmación de upgrade */}
      <Dialog.Root open={isOpen} placement='center' onOpenChange={e => {
        if (!e.open) {
          onClose();
        }
      }}>
        <Portal>

          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header>
                <HStack>
                  <Icon color="purple.500" asChild><FaCrown /></Icon>
                  <Text>Pasar al plan Avanzado</Text>
                </HStack>
              </Dialog.Header>
              <Dialog.CloseTrigger />
              <Dialog.Body>
                <Text color="gray.600" mb={3}>
                  El plan Avanzado es <b>gratis por tiempo limitado</b> durante el lanzamiento
                  e incluye todas las funcionalidades: músicos, formaciones, actuaciones,
                  ensayos, archivo musical ampliado, dominio propio y máximo almacenamiento.
                </Text>
                <List.Root gap={1}>
                  <List.Item fontSize="sm" color="gray.700"><List.Indicator color="green.500" asChild><FaCheck /></List.Indicator>Sin límites de músicos ni actuaciones</List.Item>
                  <List.Item fontSize="sm" color="gray.700"><List.Indicator color="green.500" asChild><FaCheck /></List.Indicator>Archivo musical e IA ampliados</List.Item>
                  <List.Item fontSize="sm" color="gray.700"><List.Indicator color="green.500" asChild><FaCheck /></List.Indicator>Dominio propio incluido</List.Item>
                </List.Root>
              </Dialog.Body>
              <Dialog.Footer>
                <Button variant="ghost" mr={3} onClick={onClose}>Cancelar</Button>
                <Button colorPalette="purple" onClick={handleUpgrade} loading={upgrading}>
                  Activar plan Avanzado
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>
    </Container>,
  );
};

export default MiSuscripcion;
